export const CONTACT_DETAILS_SESSION_KEY = 'salesforce_lead_contact_details';
export const CONTACT_ID_SESSION_KEY = 'salesforce_contact_id';
export const ACCOUNT_ID_SESSION_KEY = 'salesforce_account_id';
export const LEAD_ID_SESSION_KEY = 'salesforce_lead_id';
export const MEMBER_PROFILES_SESSION_KEY =
  'salesforce_member_profiles_session_key';
export const OPPORTUNITY_ID_SESSION_KEY = 'salesforce_opportunity_id';
export const PENDING_ACCOUNT_DETAILS_KEY = 'pending_account_details';
export const ANSWERS_KEY = 'formsort_answers';
import type { FormSortAnswers } from '@/types/forms';

export class SessionStorageService {
  // Helper method to safely access sessionStorage
  private getItem(key: string): string | null {
    try {
      return typeof window !== 'undefined' ? sessionStorage.getItem(key) : null;
    } catch {
      return null;
    }
  }

  private setItem(key: string, value: string): void {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(key, value);
      }
    } catch (error) {
      console.error(`Failed to set sessionStorage item ${key}:`, error);
    }
  }

  private parseJson<T>(value: string | null): T | null {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  setFormSortAnswers(answers: FormSortAnswers): void {
    this.setItem(ANSWERS_KEY, JSON.stringify(answers));
  }

  getFormSortAnswers(): FormSortAnswers | null {
    const stored = this.getItem(ANSWERS_KEY);
    return this.parseJson<FormSortAnswers>(stored);
  }

  // Lead operations
  setLeadId(leadId: string): void {
    this.setItem(LEAD_ID_SESSION_KEY, leadId);
  }

  getLeadId(): string | null {
    return this.getItem(LEAD_ID_SESSION_KEY);
  }

  // Contact ID operations
  setAccountId(contactId: string): void {
    this.setItem(ACCOUNT_ID_SESSION_KEY, contactId);
  }

  getAccountId(): string | null {
    return this.getItem(ACCOUNT_ID_SESSION_KEY);
  }

  // Contact ID operations
  setContactId(contactId: string): void {
    this.setItem(CONTACT_ID_SESSION_KEY, contactId);
  }

  getContactId(): string | null {
    return this.getItem(CONTACT_ID_SESSION_KEY);
  }

  // Opportunity ID operations
  setOpportunityId(opportunityId: string): void {
    this.setItem(OPPORTUNITY_ID_SESSION_KEY, opportunityId);
  }

  getOpportunityId(): string | null {
    return this.getItem(OPPORTUNITY_ID_SESSION_KEY);
  }

  // Pending account details (saved on Account Details, consumed on Referral Source page)
  setPendingAccountDetails(details: object): void {
    this.setItem(PENDING_ACCOUNT_DETAILS_KEY, JSON.stringify(details));
  }

  getPendingAccountDetails<T>(): T | null {
    const stored = this.getItem(PENDING_ACCOUNT_DETAILS_KEY);
    return this.parseJson<T>(stored);
  }

  clearPendingAccountDetails(): void {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(PENDING_ACCOUNT_DETAILS_KEY);
      }
    } catch (error) {
      console.error('Failed to clear pending account details:', error);
    }
  }

  // Clear methods
  clearFormSortAnswers(): void {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(ANSWERS_KEY);
      }
    } catch (error) {
      console.error('Failed to clear intake form answers:', error);
    }
  }

  clearLead(): void {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(LEAD_ID_SESSION_KEY);
      }
    } catch (error) {
      console.error('Failed to clear lead ID:', error);
    }
  }

  clearContactId(): void {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(CONTACT_ID_SESSION_KEY);
      }
    } catch (error) {
      console.error('Failed to clear contact ID:', error);
    }
  }

  clearOpportunityId(): void {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(OPPORTUNITY_ID_SESSION_KEY);
      }
    } catch (error) {
      console.error('Failed to clear opportunity ID:', error);
    }
  }

  clearAll(): void {
    this.clearFormSortAnswers();
    this.clearLead();
    this.clearContactId();
    this.clearOpportunityId();
    this.clearPendingAccountDetails();
  }
}

// Create a singleton instance
export const sessionStorageService = new SessionStorageService();
